import { useQuery } from '@tanstack/react-query';
import { FileMinus2, FileText, Landmark, Receipt, type LucideIcon } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { Cargando } from '@/shared/ui/Cargando';
import { Seccion } from '@/shared/ui/Seccion';
import { Evolucion, enDinero } from '@/shared/ui/graficos';

interface Mes {
  periodo: string;
  facturas: number;
  baseImponible: number;
  iva: number;
  facturado: number;
  notas: number;
  acreditado: number;
  neto: number;
}

interface Nota {
  numero: string;
  fecha: string;
  factura: string;
  cliente: string;
  identificacion: string;

  motivo: string;
  base: number;
  iva: number;
  total: number;
}

interface Panel {
  facturas: number;
  facturado: number;
  notas: number;
  acreditado: number;
  neto: number;
  meses: Mes[];
  detalleDeNotas: Nota[];

  notasSinAutorizar: number;
}

export function PanelDeVentas({ desde, hasta }: { desde: string; hasta: string }) {
  const panel = useQuery({
    queryKey: ['informe-ventas', desde, hasta],
    queryFn: () =>
      api.get<Panel>('/informes/facturacion/ventas/panel', { query: { desde, hasta } }),
  });

  if (panel.isLoading) {
    return <Cargando titulo="Cargando" detalle="sumando lo que el SRI autorizó" />;
  }

  const datos = panel.data;
  if (!datos || datos.meses.length === 0) {
    return (
      <p className="rounded-2xl border border-armazon-200 bg-white p-12 text-center text-sm uppercase text-armazon-400">
        El SRI no autorizó nada en el período elegido
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Cifra
          icono={FileText}
          rotulo="Facturado"
          valor={enDinero(datos.facturado)}
          tono="text-armazon-700"
          fondo="bg-armazon-50"
          ayuda={`${datos.facturas.toLocaleString('es-EC')} comprobantes autorizados`}
        />
        <Cifra
          icono={FileMinus2}
          rotulo="Acreditado"
          valor={enDinero(datos.acreditado)}
          tono="text-amber-700"
          fondo="bg-amber-50"
          ayuda={`${datos.notas.toLocaleString('es-EC')} notas de crédito. Restan del neto`}
        />
        <Cifra
          icono={Landmark}
          rotulo="Neto a declarar"
          valor={enDinero(datos.neto)}
          tono="text-marca-700"
          fondo="bg-marca-50"
          ayuda="Lo facturado menos lo acreditado"
        />
        <Cifra
          icono={Receipt}
          rotulo="Comprobantes"
          valor={datos.facturas.toLocaleString('es-EC')}
          tono="text-blue-700"
          fondo="bg-blue-50"
        />
      </div>

      <Seccion
        titulo="Neto por mes"
        ayuda="Por fecha de autorización del SRI, ya restadas las notas de crédito"
      >
        <Evolucion datos={datos.meses} claveX="periodo" claveY="neto" nombre="Neto" />
      </Seccion>

      <Seccion titulo="Detalle para el formulario" ayuda="Los mismos números, mes a mes">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-armazon-50/80">
              <tr>
                {[
                  'Mes',
                  'Facturas',
                  'Base imponible',
                  'IVA',
                  'Facturado',
                  'N. créd.',
                  'Acreditado',
                  'Neto',
                ].map((c, i) => (
                  <th
                    key={c}
                    scope="col"
                    className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-armazon-500 ${
                      i === 0 ? 'text-left' : 'text-right'
                    }`}
                  >
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-armazon-100">
              {datos.meses.map((m) => (
                <tr key={m.periodo} className="transition-colors hover:bg-armazon-50/60">
                  <td className="px-3 py-2.5 text-xs font-medium text-armazon-700">{m.periodo}</td>
                  <td className="px-3 py-2.5 text-right text-xs tabular-nums">
                    {m.facturas.toLocaleString('es-EC')}
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs tabular-nums">
                    {enDinero(m.baseImponible)}
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs tabular-nums text-armazon-400">
                    {enDinero(m.iva)}
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs tabular-nums">
                    {enDinero(m.facturado)}
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs tabular-nums text-amber-700">
                    {m.notas.toLocaleString('es-EC')}
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs tabular-nums text-amber-700">
                    {enDinero(m.acreditado)}
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs font-semibold tabular-nums text-marca-700">
                    {enDinero(m.neto)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Seccion>

      <Seccion
        titulo="Notas de crédito"
        ayuda="El detalle de lo acreditado: qué se corrigió, contra qué factura y por qué"
      >
        {datos.detalleDeNotas.length === 0 ? (
          <p className="p-8 text-center text-sm uppercase text-armazon-400">
            No se autorizó ninguna nota de crédito en el período
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-armazon-50/80">
                <tr>
                  {['Nota', 'Fecha', 'Factura', 'Cliente', 'Motivo', 'Acreditado'].map((c, i) => (
                    <th
                      key={c}
                      scope="col"
                      className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-armazon-500 ${
                        i === 5 ? 'text-right' : 'text-left'
                      }`}
                    >
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-armazon-100">
                {datos.detalleDeNotas.map((n) => (
                  <tr key={n.numero} className="transition-colors hover:bg-armazon-50/60">
                    <td className="px-3 py-2.5 text-xs font-medium tabular-nums text-armazon-700">
                      {n.numero}
                    </td>
                    <td className="px-3 py-2.5 text-xs tabular-nums text-armazon-500">{n.fecha}</td>
                    <td className="px-3 py-2.5 text-xs tabular-nums text-armazon-500">
                      {n.factura}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-armazon-700">
                      {n.cliente}
                      <span className="block text-[11px] tabular-nums text-armazon-400">
                        {n.identificacion}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-armazon-600">{n.motivo}</td>
                    <td className="px-3 py-2.5 text-right text-xs font-semibold tabular-nums text-amber-700">
                      {enDinero(n.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {datos.notasSinAutorizar > 0 && (
          <p className="border-t border-armazon-100 px-3 py-2.5 text-[11px] text-amber-700">
            {datos.notasSinAutorizar === 1
              ? 'Hay 1 nota emitida en el período que el SRI todavía no ha autorizado: no resta de la declaración.'
              : `Hay ${datos.notasSinAutorizar.toLocaleString('es-EC')} notas emitidas en el período que el SRI todavía no ha autorizado: no restan de la declaración.`}
          </p>
        )}
      </Seccion>
    </div>
  );
}

function Cifra({
  icono: Icono,
  rotulo,
  valor,
  tono,
  fondo,
  ayuda,
}: {
  icono: LucideIcon;
  rotulo: string;
  valor: string;
  tono: string;
  fondo: string;
  ayuda?: string;
}) {
  return (
    <div className={`rounded-2xl border border-white/60 p-4 ${fondo}`} title={ayuda}>
      <span className="mb-2 flex items-center gap-2">
        <Icono className={`size-4 ${tono}`} aria-hidden />
        <span className="text-[10px] font-bold uppercase tracking-wider text-armazon-500">
          {rotulo}
        </span>
      </span>
      <span className={`block truncate text-xl font-bold tabular-nums ${tono}`}>{valor}</span>
    </div>
  );
}
